import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import type { RxCollection, RxDocument } from 'rxdb';
import { RxReplicationError } from 'rxdb/dist/types/plugins/replication';
import { RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql';

import { rootStore } from '../mobx-models/Root';
import { trackEventFn } from '../utilities/hooks/useEventTracking';
import { getDocumentIdByCollectionName } from '../utilities/utilityFunctions';
import { refreshTokenOnSyncState } from './refreshToken';
import { RxdbCollectionName } from './rxdbCollectionName';

export const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';
export const DEBUG = localStorage.getItem('DEBUG') === 'true';

const unauthorizedErrorType = 'AUTH_NOT_AUTHENTICATED';
const currentTimestamp = dayjs().unix();
const checkForExpiredToken = (err: RxReplicationError<unknown>) => {
  return (
    err?.innerErrors &&
    Array.isArray(err?.innerErrors) &&
    err.innerErrors?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (innerError: any) =>
        innerError?.message?.toLowerCase().includes('not authorized') ||
        innerError?.extensions?.code === unauthorizedErrorType,
    ) !== undefined
  );
};
const hasForeignKeyErrors = (err: RxReplicationError<unknown>) => {
  // If you need to add more foreign key error handling it can be done here
  return err.innerErrors?.[0]?.extensions?.message?.includes('No Deficiency Found');
};

const recordIdsFailedToSyncCount: Record<string, number> = {};

/** @type {*} The connections between the rxdb collections and their foreignKeys */

const documentRemovalRelationships: Record<
  string,
  {
    collectionName: string;
    foreignKey: string;
  }[]
> = {
  [RxdbCollectionName.DEFICIENCY_LIST]: [
    {
      collectionName: RxdbCollectionName.RISK_ASSESSMENT,
      foreignKey: 'fkDeficiencyUUID',
    },
    {
      collectionName: RxdbCollectionName.DEFICIENCY_FILES,
      foreignKey: 'deficiencyUUID',
    },
  ],
};

/**
 * Removes a single document from rxdb, and tracks it in the event tracking system and sentry
 *
 * @param {RxDocument} document
 * @param {RxCollection<any>} collection
 * @param {string} errMessage
 * @param {string} errType
 */

const executeDocumentRemoval = async (
  document: RxDocument,
  collection: RxCollection<unknown>,
  errMessage: string,
  errType: string,
) => {
  try {
    await trackEventFn('RXDB_PUSH_FAILURE_DOC_REMOVAL', {
      collection: collection?.name || 'UNKNOWN',
      documentId: document?.[getDocumentIdByCollectionName(collection?.name)],
      document: JSON.stringify(document),
      errMessage,
      errType,
      documentRemoval: true,
    });

    if (!document.deleted) await document.remove();
    await collection.storageInstance.cleanup(0);
    if (DEBUG_RXDB) console.log('Document removed...');

    delete recordIdsFailedToSyncCount[document?.[getDocumentIdByCollectionName(collection?.name)]];
  } catch (error) {
    if (DEBUG_RXDB) {
      console.log('Error removing document...', error, document);
    }
    Sentry.captureException(error, {
      tags: {
        rxDBError: true,
        removingDocumentError: true,
        collectionName: collection?.name,
        documentRemoval: true,
        currentTimestamp,
      },
    });
  }
};

/**
 * Handles the removal of documents that have failed to sync
 * Checks if the document has any relationships that need to be removed as well
 *
 * @param {string} documentId
 * @param {RxCollection<unknown>} collection
 * @param {string} errMessage
 * @param {=} errType
 */
const handleDocumentRemoval = async (
  documentId: string,
  collection: RxCollection<unknown>,
  errMessage: string,
  errType: string,
) => {
  const primaryKey = getDocumentIdByCollectionName(collection.name);
  const originalDoc: RxDocument = await collection
    .findOne({
      selector: {
        [primaryKey]: documentId,
      },
    })
    .exec();

  if (!originalDoc) {
    if (DEBUG_RXDB) console.log('Document not found in collection...', documentId, collection);
    await collection.storageInstance.cleanup(0);
    return;
  }

  if (DEBUG_RXDB) console.log('Document to be removed...', JSON.parse(JSON.stringify(originalDoc)));

  let docsToRemove: Array<{ collection: RxCollection; document: RxDocument }> = [
    { collection, document: originalDoc },
  ];

  // Check if the document has any relationships with docs that need to be removed
  if (documentRemovalRelationships.hasOwnProperty(collection.name)) {
    const relationships = documentRemovalRelationships[collection.name];
    for (const relationship of relationships) {
      const { collectionName, foreignKey } = relationship;
      const relationshipCollection = collection.database.collections[collectionName];
      if (relationshipCollection) {
        const relatedDocs = await relationshipCollection
          .find({
            selector: {
              [foreignKey]: documentId,
            },
          })
          .exec();

        if (relatedDocs) {
          if (DEBUG_RXDB)
            console.log(
              `Found ${relatedDocs.length} related docs in ${collectionName} to remove...`,
              relatedDocs,
            );

          docsToRemove = [
            ...docsToRemove,
            ...relatedDocs.map(doc => ({ collection: relationshipCollection, document: doc })),
          ];
        }
      }
    }
  }

  if (DEBUG_RXDB) console.log('Documents to be removed...', docsToRemove);

  await Promise.all(
    docsToRemove.map(({ document: doc, collection: col }) =>
      executeDocumentRemoval(doc, col, errMessage, errType),
    ),
  );
};

const recordFailedToSyncRecord = (recordId: string) => {
  if (!recordIdsFailedToSyncCount[recordId]) {
    recordIdsFailedToSyncCount[recordId] = 1;
  }
  recordIdsFailedToSyncCount[recordId] += 1;
};

const recordIdHasFailedToSyncMoreThanXTimes = (recordId: string, xTimes = 10) => {
  return recordIdsFailedToSyncCount[recordId] >= xTimes;
};

const errorReportedToSentry = (recordId: string) => recordIdsFailedToSyncCount[recordId];

const captureRxdbExceptionInSentry = (
  err: unknown,
  collectionState: RxGraphQLReplicationState<unknown>,
  additionalFields: { [key: string]: string | boolean | undefined } = {},
) => {
  const hasExpiredToken = checkForExpiredToken(err);
  Sentry.captureException(err, {
    contexts: {
      documentData: {
        documentData: JSON.stringify(err.documentsData),
        error: JSON.stringify(err.innerErrors),
      },
    },
    extra: { documentData: JSON.stringify(err.documentsData) },
    tags: {
      rxDBError: true,
      hasExpiredToken,
      hasForeignKeyErrors: hasForeignKeyErrors(err),
      collectionName: collectionState?.collection?.name,
      currentTimestamp,
      ...additionalFields,
    },
  });
};

export const handleReplicationErrors = (collectionState: RxGraphQLReplicationState<unknown>) => {
  collectionState.error$.subscribe(async err => {
    const hasExpiredToken = checkForExpiredToken(err);

    if (hasExpiredToken) refreshTokenOnSyncState(collectionState, 6 * 60 * 60);

    if (err) {
      const { type, message } = err;
      // If debug then show the error
      if (DEBUG_RXDB) {
        console.error(`rxDB Error -  ${type}ing Data from Server`, JSON.stringify(err));
        console.dir(err);
      }

      if (!DEBUG_RXDB && message === 'Failed to fetch') {
        // Network failed dont report error
        return;
      }

      // Generic error related to network failure
      if (message === 'Load failed') {
        return;
      }

      // Check if the error is due to a foreign key constraint if it is we can allow it to keep trying to sync
      if (type === 'push' && hasForeignKeyErrors(err)) {
        captureRxdbExceptionInSentry(err, collectionState, {
          replicationPushError: true,
        });
        return;
      }

      // General logging
      if (DEBUG_RXDB) {
        console.error(`rxDB Error -  ${type}ing Data from Server`, JSON.stringify(err));
        console.dir(err);
      }

      const documentId = err.documentsData?.[0]?.[
        getDocumentIdByCollectionName(collectionState.collection.name)
      ] as string;
      const isDeficiencyCollection =
        collectionState.url.indexOf('collection=deficiency_files') >= 0
          ? 1
          : collectionState.url.indexOf('collection=inspection_files') >= 0
          ? 1
          : 0;
      if (type === 'pull') {
        // Error on pull
        if (!errorReportedToSentry(documentId)) {
          captureRxdbExceptionInSentry(err, collectionState, {
            replicationPullError: true,
          });
        }
      } else if (type === 'push') {
        if (rootStore?.appVersion?.hasNewUpdate) {
          // Don't remove documents if not on latest version
          captureRxdbExceptionInSentry(err, collectionState, {
            hasNewUpdateAvailable: true,
          });
          return;
        }

        // Error on push
        if (!errorReportedToSentry(documentId)) {
          captureRxdbExceptionInSentry(err, collectionState, {
            replicationPushError: true,
          });
        }

        // If we have gotten to this point its not a parent/child relationship error likely and we should remove and report the bad document
        if (
          !hasExpiredToken &&
          recordIdHasFailedToSyncMoreThanXTimes(documentId, isDeficiencyCollection ? 5 : 10)
        ) {
          captureRxdbExceptionInSentry(err, collectionState, {
            replicationPushError: true,
            documentRemoval: true,
          });
          await handleDocumentRemoval(
            documentId,
            collectionState.collection,
            JSON.stringify(err),
            type,
          );
        }

        // Increase failure count
        recordFailedToSyncRecord(documentId);
      } else {
        // Error on unknown
        captureRxdbExceptionInSentry(err, collectionState, {
          replicationUnknownError: true,
        });
      }
    } else {
      // General error, like local DB connection issue
      if (DEBUG_RXDB) {
        console.error('rxDB Error - General Error', JSON.stringify(err));
        console.dir(err);
      }
      captureRxdbExceptionInSentry(err, collectionState, {
        replicationPushError: true,
      });
    }
  });
};
